/**
 * GPU particle system. Ambient motes live at the front of the buffer and are
 * simulated + wrapped entirely on the GPU; transient effects are written by the
 * CPU into a ring at the back. Only the dirty range is uploaded each frame.
 */

import shader from './shaders/particles.wgsl?raw';
import { makeStorage, makeUniform } from './gpu';

export const KIND = {
  ambient: 0,
  bubble: 1,
  spark: 2,
  debris: 3,
  cloud: 4,
  /**
   * Plankton — the bright specks, as against `ambient`, which is the silt haze.
   *
   * Simulated identically: the shader normalises 5 to 0 the moment it reads the
   * tag, so every branch treats it as ambient. The separate number exists ONLY
   * so the two can be switched apart in the kind mask. They were seeded from
   * the same loop and shared a number, which made it impossible to say from
   * outside which of the two anything on screen belonged to.
   */
  plankton: 5,
  /** Ballistic water droplets above the surface. Kept apart from bubbles. */
  spray: 6,
} as const;

const STRIDE = 16; // floats per particle

export interface EmitOpts {
  kind: number;
  x: number; y: number; z?: number;
  vx?: number; vy?: number; vz?: number;
  life: number;
  size: number;
  r: number; g: number; b: number;
  glow?: number;
  drag?: number;
}

export class Particles {
  readonly buffer: GPUBuffer;
  private simU: GPUBuffer;
  private simPipeline: GPUComputePipeline;
  private simBind: GPUBindGroup;
  readonly renderModule: GPUShaderModule;
  /** The specialised source, so the renderer can ask the device about it. */
  readonly source!: string;

  private staging: Float32Array<ArrayBuffer>;
  private cursor: number;
  private dirtyLo = Infinity;
  private dirtyHi = -Infinity;
  private simData: Float32Array<ArrayBuffer> = new Float32Array(20);

  constructor(
    private device: GPUDevice,
    readonly ambientCount = 24000,
    readonly dynamicCount = 12000,
    /**
     * Specialises the source the same way the scene's is — the particle pass
     * writes motion vectors alongside colour under TAA, and the simulation
     * shares this module, so leaving the placeholders in makes the COMPUTE
     * pipeline invalid too and the whole frame's command buffer with it.
     */
    specialise: (src: string) => string = (src) => src,
    /** The wake field, sampled by the sim so the water can carry things. */
    wake?: GPUTextureView,
  ) {
    const total = ambientCount + dynamicCount;
    this.buffer = makeStorage(device, total * STRIDE * 4, 'particles');
    this.staging = new Float32Array(total * STRIDE);
    this.cursor = ambientCount;

    this.seedAmbient();
    device.queue.writeBuffer(this.buffer, 0, this.staging);

    const code = ((this as { source: string }).source = specialise(shader));
    const module = device.createShaderModule({ code, label: 'particles' });
    this.renderModule = module;
    this.simU = makeUniform(device, 80, 'particle-sim');
    this.simPipeline = device.createComputePipeline({
      layout: 'auto',
      compute: { module, entryPoint: 'simulate' },
      label: 'particle-sim',
    });
    this.simBind = device.createBindGroup({
      layout: this.simPipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: this.buffer } },
        { binding: 1, resource: { buffer: this.simU } },
        // The wake, so suspended matter is carried by the water rather than
        // hanging still in it. This is what stops the field being a filter
        // over the scene and makes it part of it.
        ...(wake ? [{ binding: 2, resource: wake }] : []),
      ],
    });
  }

  get total() {
    return this.ambientCount + this.dynamicCount;
  }

  private seedAmbient() {
    const s = this.staging;
    for (let i = 0; i < this.ambientCount; i++) {
      const o = i * STRIDE;
      s[o] = (Math.random() * 2 - 1) * 90;
      s[o + 1] = (Math.random() * 2 - 1) * 55;
      s[o + 2] = (Math.random() * 2 - 1) * 80;
      s[o + 3] = 1;
      s[o + 4] = 0; s[o + 5] = 0; s[o + 6] = 0; s[o + 7] = 1;

      // 87 % dull sediment, 13 % glowing plankton (PRD §2.3). Plankton are the
      // only motes that are genuinely their own light source, so they are the
      // ones the eye locks onto — at a fifth of the field they read as the
      // occasional live thing drifting past, which is what they are. At a
      // quarter they were most of what you saw.
      const plankton = Math.random() < 0.13;
      if (plankton) {
        const warm = Math.random();
        s[o + 8] = 0.30 + warm * 0.5;
        s[o + 9] = 0.85 + warm * 0.15;
        s[o + 10] = 0.95;
        s[o + 11] = 0.028 + Math.random() * 0.055;
        s[o + 15] = 1; // additive
        // KIND 5 for plankton. Same simulation as silt — the sim branches on
        // `< 0.5` and both are ambient — but a distinct tag so the two can be
        // switched independently. One of them is the pretty bright specks and
        // the other is the haze, and telling them apart from the outside was
        // impossible while they shared a number.
      } else {
        // Silt is additive, like the plankton. It was alpha-blended, which
        // means a mote dimmer than the water behind it drew as a dark disc —
        // fine while they were a pixel across and invisible, immediately a
        // screenful of dark confetti once they were big enough to see. Nothing
        // suspended in water can subtract light from it: silt scatters, so it
        // only ever adds. Faint and a little larger, it reads as haze.
        // Cubed, so the distribution is bottom-heavy: most grains are barely
        // there and a few are properly visible. A narrow range — this used to
        // be 0.30..0.58 — gives every mote the same value, and a field where
        // everything is equally bright has no depth in it however the sizes
        // are scattered, because brightness is the cue the eye trusts first.
        const u = Math.random();
        const v = 0.10 + u * u * u * 0.62;
        s[o + 8] = v * 0.86;
        s[o + 9] = v * 0.93;
        s[o + 10] = v;
        s[o + 11] = 0.075 + Math.random() * 0.19;
        s[o + 15] = 1;
      }
      s[o + 12] = plankton ? KIND.plankton : KIND.ambient;
      s[o + 13] = 0;
      s[o + 14] = Math.random() * 100;
    }
  }

  /**
   * The z plane one-shot effects happen in — the lane the sub is flying.
   *
   * Every emitter jitters z around zero, which was the only plane there was.
   * Set here rather than threaded through forty call sites: bubbles, sparks,
   * silt and explosions are overwhelmingly things happening AT the boat, and
   * the alternative is a z argument on each one that every future effect has
   * to remember to pass. Ambient motes are unaffected — they are simulated on
   * the GPU and fill the water around the camera on their own.
   */
  baseZ = 0;

  emit(o: EmitOpts) {
    const i = this.cursor;
    this.cursor++;
    if (this.cursor >= this.total) this.cursor = this.ambientCount;
    const b = i * STRIDE;
    const s = this.staging;
    s[b] = o.x; s[b + 1] = o.y; s[b + 2] = this.baseZ + (o.z ?? 0); s[b + 3] = o.life;
    s[b + 4] = o.vx ?? 0; s[b + 5] = o.vy ?? 0; s[b + 6] = o.vz ?? 0; s[b + 7] = o.life;
    s[b + 8] = o.r; s[b + 9] = o.g; s[b + 10] = o.b; s[b + 11] = o.size;
    s[b + 12] = o.kind; s[b + 13] = o.drag ?? 1.2; s[b + 14] = Math.random() * 100;
    s[b + 15] = o.glow ?? 0;
    if (i < this.dirtyLo) this.dirtyLo = i;
    if (i > this.dirtyHi) this.dirtyHi = i;
  }

  // --- convenience emitters -------------------------------------------------

  bubbles(x: number, y: number, n: number, spread = 1, speed = 2, size = 0.14) {
    for (let i = 0; i < n; i++) {
      this.emit({
        kind: KIND.bubble,
        x: x + (Math.random() - 0.5) * spread,
        y: y + (Math.random() - 0.5) * spread,
        z: (Math.random() - 0.5) * spread,
        vx: (Math.random() - 0.5) * speed,
        vy: (Math.random() - 0.5) * speed + 1,
        vz: (Math.random() - 0.5) * speed,
        life: 1.6 + Math.random() * 2.6,
        size: size * (0.5 + Math.random()),
        r: 0.75, g: 0.92, b: 1.0,
        /**
         * AND THE LAST ONE. Fourth time, same mistake, same argument.
         *
         * A census of the live pool found 10,685 of the 12,000 emitted slots
         * holding bubbles — by a wide margin the most numerous thing in the
         * game — every one of them keeping 65 percent of its alpha, and every
         * one of them multiplied down by the ambient until it is darker than a
         * sunlit rock. Individually 0.29 m and unremarkable; ten thousand of
         * them overlapping is a field of dark circles, which is exactly what
         * it looked like.
         *
         * An air bubble in water is BRIGHT. Total internal reflection at the
         * boundary makes it silvery — it is the one thing down here that is
         * more obviously a light-scatterer than silt is. There was never a
         * reading where it made the water behind it darker.
         *
         * That leaves `ink` and explosion debris as the only alpha-blended
         * particles left, which is right: one is there to block light and the
         * other is opaque wreckage.
         */
        glow: 1,
        drag: 1.1,
      });
    }
  }

  /**
   * Ballistic white water from a breach.
   *
   * Bubbles accelerate upward and clouds hang, so neither can draw a droplet
   * that rises, turns over and falls back into the sea. Spray has its own kind
   * so the compute shader can give it gravity and the vertex shader can stretch
   * it along its velocity into a bright streak instead of a round bubble.
   */
  spray(
    x: number, y: number, n: number, speed: number, out: boolean,
    driftX = 0, driftY = 0,
  ) {
    // The surface supplies the upward impulse, but it does not erase the
    // velocity that brought the hull through it. Keeping both components is
    // what separates an oblique breach from the same vertical fountain played
    // at a different x coordinate.
    const inheritedX = driftX * (out ? 0.62 : 0.38);
    const inheritedY = out
      ? Math.max(0, driftY) * 0.54
      : Math.max(0, -driftY) * 0.12;
    for (let i = 0; i < n; i++) {
      const side = Math.random() < 0.5 ? -1 : 1;
      const lateral = out
        ? (Math.random() - 0.5) * speed * 0.58
        : side * speed * (0.24 + Math.random() * 0.60);
      const lift = out
        ? speed * (0.58 + Math.random() * 0.55)
        : speed * (0.28 + Math.random() * 0.46);
      this.emit({
        kind: KIND.spray,
        x: x + (Math.random() - 0.5) * (out ? 2.2 : 3.4),
        y: y + Math.random() * 0.35,
        z: (Math.random() - 0.5) * (out ? 2.8 : 5.0),
        vx: lateral + inheritedX,
        vy: lift + inheritedY,
        vz: (Math.random() - 0.5) * speed * 0.72,
        life: (out ? 0.34 : 0.46) + Math.random() * (out ? 0.30 : 0.38),
        size: (0.035 + Math.random() * 0.085) * (0.8 + Math.min(1.35, speed / 11)),
        // HDR on purpose. Particle lighting scales non-emissive colour by the
        // ambient water level before the additive blend; values around one
        // made surface spray dimmer than the sea it was meant to silhouette.
        r: 1.15, g: 1.40, b: 1.58,
        glow: 0.72,
        drag: out ? 0.58 : 0.46,
      });
    }
  }

  sparks(x: number, y: number, nx: number, ny: number, n: number, power = 1) {
    for (let i = 0; i < n; i++) {
      const a = Math.atan2(ny, nx) + (Math.random() - 0.5) * 2.2;
      const sp = (3 + Math.random() * 11) * power;
      this.emit({
        kind: KIND.spark,
        x, y, z: (Math.random() - 0.5) * 1.2,
        vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, vz: (Math.random() - 0.5) * 3,
        life: 0.25 + Math.random() * 0.5,
        size: 0.06 + Math.random() * 0.09,
        r: 1.0, g: 0.72, b: 0.34,
        glow: 1,
        drag: 2.4,
      });
    }
  }

  /**
   * A jelly coming apart.
   *
   * Tissue does not spark and it does not explode — the old death was 26 orange
   * sparks, the same effect a bullet makes on rock, which is why it read as
   * nothing happening. This is three things at once: a fast ring of bright
   * bioluminescent motes thrown outward, a slow cloud of drifting tissue in the
   * animal's own colour that hangs and fades, and a few long streamers for the
   * tentacles letting go. All cold and all glowing, because the one thing these
   * animals do is make their own light — and killing one should look like
   * spending it.
   */
  bioBurst(x: number, y: number, scale: number, r: number, g: number, b: number) {
    const n = Math.round(70 * scale);
    // The flash: outward, fast, bright.
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = (5 + Math.random() * 22) * scale;
      this.emit({
        kind: KIND.spark,
        x, y, z: (Math.random() - 0.5) * 4 * scale,
        vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, vz: (Math.random() - 0.5) * sp * 0.6,
        life: 0.4 + Math.random() * 1.1,
        size: (0.08 + Math.random() * 0.22) * scale,
        r, g, b,
        glow: 1,
        drag: 2.2,
      });
    }
    // The tissue: slow, large, lingering.
    for (let i = 0; i < Math.round(26 * scale); i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = (0.6 + Math.random() * 4) * scale;
      this.emit({
        kind: KIND.cloud,
        x: x + (Math.random() - 0.5) * 2 * scale,
        y: y + (Math.random() - 0.5) * 2 * scale,
        z: (Math.random() - 0.5) * 5,
        vx: Math.cos(a) * sp, vy: Math.sin(a) * sp * 0.6 + 0.8, vz: (Math.random() - 0.5) * 2,
        life: 2.2 + Math.random() * 3.4,
        size: (0.6 + Math.random() * 1.7) * scale,
        r: r * 0.7, g: g * 0.7, b: b * 0.8,
        /**
         * THESE WERE THE FLOATING DARK CIRCLES, and they were the last
         * alpha-blended thing in the game big enough to be one.
         *
         * `KIND.cloud`, so the vertex grows them 2.6x: 0.6-2.3 m at birth
         * becomes up to 6 m, twenty-six per burst, living 5.6 s. At 0.55 they
         * kept 45 percent of their alpha, and their colour is the creature's
         * at 70 percent and then multiplied DOWN by the ambient — so over a
         * sunlit rock each one is a six-metre hole. A kelp forest full of
         * bioluminescent life fires these constantly, which is why they were
         * everywhere and why they drifted.
         *
         * This is BIOLUMINESCENCE. It is light-emitting matter suspended in
         * water; there is no reading of it in which it makes the water behind
         * it darker. Additive, like the silt and the sediment before it — the
         * third time this exact mistake has been found in this file, and the
         * argument has been the same every time.
         */
        glow: 1,
        drag: 1.1,
      });
    }
    // The tentacles: a few long, slow streamers falling away.
    for (let i = 0; i < Math.round(10 * scale); i++) {
      const a = Math.PI * 0.5 + (Math.random() - 0.5) * 1.8;
      const sp = (1 + Math.random() * 5) * scale;
      this.emit({
        kind: KIND.spark,
        x: x + (Math.random() - 0.5) * 3 * scale,
        y, z: (Math.random() - 0.5) * 4,
        vx: Math.cos(a) * sp * 0.5, vy: -Math.abs(Math.sin(a)) * sp * 0.7, vz: 0,
        life: 1.6 + Math.random() * 1.8,
        size: (0.05 + Math.random() * 0.10) * scale,
        r, g, b,
        // Same argument, and it is a glowing mote. Additive.
        glow: 1,
        drag: 0.5,
      });
    }
  }

  sediment(x: number, y: number, n: number, power = 1) {
    for (let i = 0; i < n; i++) {
      this.emit({
        kind: KIND.cloud,
        x: x + (Math.random() - 0.5) * 2.5,
        y: y + (Math.random() - 0.5) * 1.5,
        z: (Math.random() - 0.5) * 3,
        vx: (Math.random() - 0.5) * 5 * power,
        vy: Math.random() * 2.4 * power,
        vz: (Math.random() - 0.5) * 4,
        life: 1.6 + Math.random() * 2.8,
        size: 0.5 + Math.random() * 1.5,
        r: 0.40, g: 0.37, b: 0.31,
        /**
         * MOSTLY ADDITIVE, AND THIS IS THE SAME ARGUMENT THE SILT ABOVE MAKES.
         *
         * It was 0 — pure alpha blend — on a particle that is 0.5 to 2 m at
         * birth and grows to 2.6x that, coloured 0.40/0.37/0.31 and then
         * multiplied down by the ambient. Something dark, huge and opaque,
         * alpha-blended over a lit rock, is a hole in the rock. Nine a second
         * are seeded along the seabed for ambience alone, each living up to
         * 4.4 s, so roughly forty of them drift over the floor at any moment —
         * the field of dark circles, and the reason it vanishes when the rock
         * goes dark: a hole is only visible against something brighter.
         *
         * Sediment IS silt. It is silt kicked off the bottom, and the note
         * fifteen lines up already settled what silt does: it scatters, so it
         * only ever adds. The argument is stronger here, not weaker, because
         * these are metres across rather than centimetres.
         *
         * 1, NOT 0.8. I hedged at 0.8 reasoning that a dense plume occludes,
         * and 0.8 still leaves 0.11 of alpha on a particle metres across with
         * forty of them overlapping — which is most of the hole, kept for a
         * case that does not need it.
         *
         * A plume of stirred silt in lit water is BRIGHTER than the water, not
         * darker. Its density shows as many overlapping ADDITIVE grains piling
         * up, which is what silt actually does to light, and it is how the
         * ambient motes above have worked since the same mistake was found in
         * them. There is no reading of a lone grain of sand suspended in front
         * of a rock where it makes the rock darker.
         *
         * `ink` stays at 0. Ink is the one thing down here that genuinely is
         * there to block light, and it should keep every bit of its alpha.
         */
        glow: 1,
        drag: 2.0,
      });
    }
  }

  explosion(x: number, y: number, scale = 1) {
    for (let i = 0; i < 46 * scale; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = (6 + Math.random() * 26) * scale;
      this.emit({
        kind: KIND.spark,
        x, y, z: (Math.random() - 0.5) * 3,
        vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, vz: (Math.random() - 0.5) * sp * 0.5,
        life: 0.3 + Math.random() * 0.9,
        size: (0.14 + Math.random() * 0.4) * scale,
        r: 1.0, g: 0.62 + Math.random() * 0.3, b: 0.28,
        glow: 1,
        drag: 3.0,
      });
    }
    this.bubbles(x, y, Math.round(40 * scale), 4 * scale, 9 * scale, 0.35 * scale);
    for (let i = 0; i < 18 * scale; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = (2 + Math.random() * 9) * scale;
      this.emit({
        kind: KIND.debris,
        x, y, z: (Math.random() - 0.5) * 3,
        vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, vz: (Math.random() - 0.5) * 4,
        life: 1.2 + Math.random() * 2.2,
        size: 0.1 + Math.random() * 0.22,
        r: 0.28, g: 0.26, b: 0.24,
        glow: 0,
        drag: 1.4,
      });
    }
    this.sediment(x, y, Math.round(16 * scale), scale);
  }

  ink(x: number, y: number, n = 40) {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 1 + Math.random() * 7;
      this.emit({
        kind: KIND.cloud,
        x, y, z: (Math.random() - 0.5) * 4,
        vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, vz: (Math.random() - 0.5) * 3,
        life: 3 + Math.random() * 4,
        size: 1.4 + Math.random() * 2.6,
        r: 0.05, g: 0.05, b: 0.08,
        glow: 0,
        drag: 1.5,
      });
    }
  }

  // --- per-frame ------------------------------------------------------------

  flush() {
    if (this.dirtyHi < this.dirtyLo) return;
    const lo = this.dirtyLo, hi = this.dirtyHi;
    this.device.queue.writeBuffer(
      this.buffer,
      lo * STRIDE * 4,
      this.staging,
      lo * STRIDE,
      (hi - lo + 1) * STRIDE,
    );
    this.dirtyLo = Infinity;
    this.dirtyHi = -Infinity;
  }

  simulate(
    enc: GPUCommandEncoder,
    camX: number, camY: number,
    halfW: number, halfH: number, halfZ: number,
    dt: number, time: number,
    flowX: number, flowY: number,
    px: number, py: number, speed: number,
    /** The wake's own world window — NOT the particle box, which is padded. */
    wakeHalfW: number, wakeHalfH: number,
  ) {
    const d = this.simData;
    d[0] = camX; d[1] = camY; d[2] = 0; d[3] = halfW;
    d[4] = halfH; d[5] = halfZ; d[6] = dt; d[7] = time;
    d[8] = flowX; d[9] = flowY; d[10] = 0; d[11] = this.ambientCount;
    d[12] = px; d[13] = py; d[14] = 0; d[15] = speed;
    d[16] = camX; d[17] = camY; d[18] = wakeHalfW; d[19] = wakeHalfH;
    this.device.queue.writeBuffer(this.simU, 0, d);

    this.flush();

    const pass = enc.beginComputePass({ label: 'particles' });
    pass.setPipeline(this.simPipeline);
    pass.setBindGroup(0, this.simBind);
    pass.dispatchWorkgroups(Math.ceil(this.total / 64));
    pass.end();
  }
}
